import useSeo from "@/lib/useSeo";
import { Reveal, SectionHeading } from "@/components/site/Reveal";
import ExportInquiry from "@/components/sections/ExportInquiry";
import { EXP, MAKHANA_GRADES, PACKAGING_OPTIONS, MARKETS, MAKHANA_FAQ, COMPLIANCE_NOTE, MAKHANA_SUPPLY } from "@/data/content";
import { scrollToId } from "@/lib/scroll";
import { waLink } from "@/lib/contactLinks";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Check, ArrowRight, MessageCircle, Building2, Boxes, FileCheck2, MapPin, FileDown } from "lucide-react";

const BUYERS = ["Importers", "Distributors", "Wholesalers", "Food Companies", "Retail / Private-Label", "International Trading Companies", "Supermarket / Foodservice"];

export default function MakhanaExport() {
  useSeo({
    title: "Indian Makhana Exporter & Supplier | AquaFionix",
    description: "AquaFionix develops export-oriented Indian Makhana (Fox Nuts) supply for global B2B buyers — bulk, multiple grades, export packaging, private label and buyer-specific specifications.",
  });

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <img src={EXP.makhanaBowl} alt="Premium Indian Makhana fox nuts for export" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#030D18] via-[#030D18]/85 to-[#030D18]/50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <span className="text-xs font-semibold uppercase tracking-[0.24em] text-amber-300">Makhana / Fox Nuts</span>
          <h1 className="font-display mt-3 max-w-3xl text-4xl sm:text-5xl font-extrabold text-white leading-tight">
            Indian Makhana <span className="text-gradient-gold">Exporter & Supplier</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">
            Export-oriented supply of premium Indian Makhana for international importers, distributors, wholesalers and food companies.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={() => scrollToId("inquiry")} data-testid="mk-request-quote" className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-7 py-3.5 text-sm font-semibold text-[#1a0f00] hover:-translate-y-0.5 transition-all">Request Makhana Quote <ArrowRight className="h-4 w-4" /></button>
            <a href={waLink("Hello AquaFionix, I'd like a Makhana export quote.")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-semibold text-[#052e16] hover:-translate-y-0.5 transition-all"><MessageCircle className="h-4 w-4" /> WhatsApp Us</a>
            <a href="/AquaFionix_Makhana_Fox_Nuts_Export_Catalogue.pdf" target="_blank" rel="noreferrer" download data-testid="mk-download-catalogue" className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 py-3.5 text-sm font-semibold text-white backdrop-blur hover:bg-white/10 transition-all"><FileDown className="h-4 w-4 text-amber-300" /> Download Makhana Catalogue</a>
          </div>
        </div>
      </section>

      {/* About Indian Makhana + Our supply */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
          <Reveal>
            <SectionHeading number="01" eyebrow="About Indian Makhana" title="A premium Indian superfood with growing global demand."
              subtitle="Makhana (fox nuts / lotus seeds) is a light, roasted-friendly snack and ingredient valued across health, snacking and food-manufacturing categories worldwide." />
            <p className="mt-4 text-sm text-slate-400 leading-relaxed">India's Mithila region in Bihar is the primary makhana-growing belt, and AquaFionix focuses on export-oriented sourcing and supply coordination for international B2B buyers.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <img src={EXP.makhanaMacro} alt="Premium Indian makhana (fox nuts) close-up" className="h-[380px] w-full object-cover rounded-[1.75rem] border border-white/10" />
          </Reveal>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
          <SectionHeading number="02" eyebrow="Our Makhana Supply" title="Built for commercial, export-oriented orders." />
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {MAKHANA_SUPPLY.map((s, i) => (
              <div key={s} className="rounded-2xl border border-white/10 bg-[#071728]/70 p-5">
                <span className="font-mono-tech text-xs text-amber-400/70">{String(i + 1).padStart(2, "0")}</span>
                <p className="mt-2 flex items-start gap-2 text-sm text-slate-200"><Check className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" />{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Grades + Packaging */}
      <section className="py-16 lg:py-24 bg-[#04121f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <SectionHeading number="03" eyebrow="Available Grades / Sizes" title="Graded by Suta & pop size (mm)." subtitle="Grades and sizes are subject to availability and confirmed against each order." />
            <div className="mt-6 flex flex-wrap gap-2">
              {MAKHANA_GRADES.map((g) => <span key={g} className="rounded-full border border-amber-400/30 bg-amber-950/30 px-4 py-2 text-sm font-medium text-amber-100">{g}</span>)}
            </div>
            <img src={EXP.makhanaJars} alt="Makhana graded by Suta — 4 Suta, 5+ Suta and 6 Suta handpicked" className="mt-6 h-56 w-full object-cover rounded-2xl border border-white/10" />
          </Reveal>
          <Reveal delay={0.1}>
            <SectionHeading number="04" eyebrow="Packaging Options" title="Pouch-packed & buyer-specific." />
            <img src={EXP.makhanaPouch} alt="Pouch-packed makhana fox nuts" className="mt-6 h-52 w-full object-cover rounded-2xl border border-white/10" />
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {PACKAGING_OPTIONS.map((p) => (
                <div key={p.title} className="rounded-2xl border border-white/10 bg-[#071728]/70 p-4">
                  <Boxes className="h-5 w-5 text-cyan-300" />
                  <p className="mt-2 text-sm font-semibold text-white">{p.title}</p>
                  <p className="mt-1 text-xs text-slate-400">{p.desc}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Bulk & commercial + Private label + Buyers */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-6 md:grid-cols-2">
          <Reveal className="rounded-3xl border border-white/10 bg-[#071728] p-7">
            <Building2 className="h-6 w-6 text-cyan-300" />
            <h3 className="mt-4 font-display text-xl font-bold text-white">Bulk & Commercial Supply</h3>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">We focus on commercial quantities for B2B buyers, with supply coordinated around buyer specifications and destination requirements. MOQ and pricing are shared per inquiry.</p>
          </Reveal>
          <Reveal delay={0.1} className="rounded-3xl border border-white/10 bg-[#071728] p-7">
            <Boxes className="h-6 w-6 text-amber-300" />
            <h3 className="mt-4 font-display text-xl font-bold text-white">Private Label / Custom Packaging</h3>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">Custom packaging and private-label options are available, subject to order requirements — supporting retail and food-company buyers who need their own branding.</p>
          </Reveal>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">Buyers We Serve</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {BUYERS.map((b) => <span key={b} className="rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm text-slate-200">{b}</span>)}
          </div>
        </div>
      </section>

      {/* Export markets + UAE */}
      <section className="py-16 lg:py-24 bg-[#04121f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading number="07" eyebrow="Export Markets" title="Markets we aim to serve." subtitle="Target markets — AquaFionix is developing supply toward these markets. This does not imply existing exports unless confirmed." />
          <div className="mt-6 flex flex-wrap gap-2">
            {MARKETS.map((m) => <span key={m} className="rounded-full border border-cyan-400/25 bg-cyan-950/30 px-4 py-2 text-sm text-cyan-100">{m}</span>)}
          </div>
          <div className="mt-10 relative overflow-hidden rounded-[2rem] border border-white/10">
            <img src={EXP.uae} alt="UAE market — Dubai skyline" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-[#030D18]/85" />
            <div className="relative p-8 sm:p-12 max-w-2xl">
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-amber-300"><MapPin className="h-4 w-4" /> Focus Market</span>
              <h3 className="mt-3 font-display text-2xl sm:text-3xl font-bold text-white">Makhana Supply for UAE Buyers</h3>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed">AquaFionix is developing export-oriented Makhana supply for UAE importers, distributors, wholesalers and food businesses.</p>
              <a href={waLink("Hello AquaFionix, I'm a UAE buyer interested in Makhana supply.")} target="_blank" rel="noreferrer" data-testid="uae-buyer-inquiry" className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-7 py-3.5 text-sm font-semibold text-[#1a0f00] hover:-translate-y-0.5 transition-all">UAE Buyer Inquiry <ArrowRight className="h-4 w-4" /></a>
            </div>
          </div>
        </div>
      </section>

      {/* Quality & documentation */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-white/10 bg-[#071728] p-7 sm:p-10 max-w-4xl">
            <FileCheck2 className="h-7 w-7 text-emerald-400" />
            <h3 className="mt-4 font-display text-2xl font-bold text-white">Quality & Documentation</h3>
            <p className="mt-3 text-base text-slate-300 leading-relaxed">{COMPLIANCE_NOTE}</p>
          </div>
        </div>
      </section>

      {/* Buyer inquiry */}
      <section className="py-16 lg:py-24 bg-[#04121f]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading number="09" eyebrow="Buyer Inquiry" title="Request Makhana product & commercial information." align="center" />
          <div className="mt-10"><ExportInquiry id="inquiry" heading="Makhana Buyer Inquiry" defaultProduct="Makhana / Fox Nuts" /></div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading number="10" eyebrow="FAQ" title="Frequently asked questions." align="center" />
          <Accordion type="single" collapsible className="mt-8">
            {MAKHANA_FAQ.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
                <AccordionTrigger className="text-left text-slate-100 hover:text-white" data-testid={`faq-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-400">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
